// src/pages/Legal/CookiePolicyPage.tsx
import React, { useState } from 'react';
import MainLayout from '../../components/MainLayout/MainLayout';
import { 
  Cookie, 
  Settings, 
  BarChart3, 
  Shield, 
  Target, 
  Globe,
  CheckCircle,
  XCircle,
  Info,
  Calendar,
  ChevronRight,
  FileText,
  ToggleLeft,
  ToggleRight,
  Eye,
  Zap
} from 'lucide-react';

/**
 * PLATFORM STATUS: Single Creator Model (Current)
 * 
 * Cookie policy is largely unchanged as it applies to all users regardless of
 * the creator model. Minor updates for version tracking.
 * 
 * Version: 1.0.0 - Single Creator Model
 * Next Version: 2.0.0 - Creator Open Model (planned - may include creator-specific cookies)
 */

const CookiePolicyPage = () => {
  const [activeSection, setActiveSection] = useState<string>('');
  const [cookieSettings, setCookieSettings] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: true,
    marketing: false,
    functional: true
  });

  const tableOfContents = [
    { id: 'what-are-cookies', title: 'What Are Cookies?' },
    { id: 'types-of-cookies', title: 'Types of Cookies We Use' },
    { id: 'cookie-categories', title: 'Cookie Categories' },
    { id: 'third-party-cookies', title: 'Third-Party Cookies' },
    { id: 'managing-cookies', title: 'Managing Your Cookies' },
    { id: 'browser-controls', title: 'Browser Controls' },
    { id: 'mobile-devices', title: 'Mobile Device Settings' },
    { id: 'updates', title: 'Policy Updates' },
    { id: 'contact', title: 'Contact Us' }
  ];

  const cookieTypes = [
    {
      icon: Shield,
      title: 'Necessary Cookies',
      description: 'Essential for the website to function properly',
      required: true,
      examples: ['User authentication', 'Security tokens', 'Session management', 'Load balancing'],
      color: 'green'
    },
    {
      icon: Zap,
      title: 'Functional Cookies',
      description: 'Enable enhanced functionality and personalization',
      required: false,
      examples: ['Language preferences', 'Theme settings', 'Volume controls', 'Accessibility options'],
      color: 'blue'
    },
    {
      icon: BarChart3,
      title: 'Analytics Cookies',
      description: 'Help us understand how visitors interact with our website',
      required: false,
      examples: ['Page views', 'Time spent', 'User behavior', 'Error tracking'],
      color: 'purple'
    },
    {
      icon: Target,
      title: 'Marketing Cookies',
      description: 'Used to deliver personalized advertising',
      required: false,
      examples: ['Ad targeting', 'Campaign tracking', 'Social media integration', 'Remarketing'],
      color: 'pink'
    }
  ];

  const thirdPartyServices = [
    {
      name: 'Google Analytics',
      purpose: 'Website analytics and user behavior tracking',
      type: 'Analytics',
      dataShared: 'Anonymous usage data, page views, session duration',
      optOut: 'https://tools.google.com/dlpage/gaoptout'
    },
    {
      name: 'YouTube',
      purpose: 'Video content delivery and analytics',
      type: 'Functional/Analytics',
      dataShared: 'Video viewing behavior, device information',
      optOut: 'Google privacy settings'
    },
    {
      name: 'Stripe',
      purpose: 'Payment processing and fraud prevention',
      type: 'Necessary',
      dataShared: 'Payment information, transaction data',
      optOut: 'Cannot opt out for payment functionality'
    },
    {
      name: 'Intercom',
      purpose: 'Customer support chat functionality',
      type: 'Functional',
      dataShared: 'User messages, support interactions',
      optOut: 'Chat widget settings'
    }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  const handleCookieToggle = (category: string) => {
    if (category === 'necessary') return; // Cannot disable necessary cookies
    
    setCookieSettings(prev => ({
      ...prev,
      [category]: !prev[category as keyof typeof prev]
    }));
  };

  const saveCookiePreferences = () => {
    // In a real implementation, this would save to localStorage and update actual cookie settings
    localStorage.setItem('cookiePreferences', JSON.stringify(cookieSettings));
    alert('Cookie preferences saved!');
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-950 via-neutral-900 to-slate-950 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center">
                  <Cookie className="h-8 w-8 text-white" />
                </div>
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                Cookie <span className="text-transparent font-display bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text">Policy</span>
              </h1>
              
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                Learn about how we use cookies and similar technologies to enhance your experience 
                on our platform and protect your privacy.
              </p>
              
              <div className="flex items-center justify-center space-x-4 text-sm text-gray-300">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>Last updated: December 2025</span>
                </div>
                <div className="flex items-center">
                  <FileText className="h-4 w-4 mr-2" />
                  <span>Version: 1.0.0</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="lg:grid lg:grid-cols-4 lg:gap-8">
            {/* Table of Contents */}
            <div className="lg:col-span-1">
              <div className="sticky top-8">
                <div className="bg-white rounded-2xl p-6 shadow-lg mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Table of Contents</h3>
                  <nav className="space-y-1">
                    {tableOfContents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors duration-200 flex items-center ${
                          activeSection === item.id
                            ? 'bg-orange-100 text-orange-700 font-medium'
                            : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
                        }`}
                      >
                        <ChevronRight className="h-4 w-4 mr-2" />
                        {item.title}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Quick Cookie Settings */}
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Cookie Preferences</h3>
                  <div className="space-y-3">
                    {Object.entries(cookieSettings).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <span className="text-sm text-gray-700 capitalize">
                          {key === 'necessary' ? 'Necessary (Required)' : key}
                        </span>
                        <button
                          onClick={() => handleCookieToggle(key)}
                          disabled={key === 'necessary'}
                          className={`${
                            value ? 'text-green-600' : 'text-gray-400'
                          } ${key === 'necessary' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          {value ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={saveCookiePreferences}
                      className="w-full mt-4 px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors"
                    >
                      Save Preferences
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-3 mt-8 lg:mt-0">
              
              {/* What Are Cookies */}
              <section id="what-are-cookies" className="mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <Cookie className="h-6 w-6 text-orange-500 mr-3" />
                    What Are Cookies?
                  </h2>
                  <div className="text-gray-700 space-y-4">
                    <p>
                      Cookies are small text files that are stored on your device when you visit our website. 
                      They help us provide you with a better experience by remembering your preferences, 
                      keeping you logged in, and analyzing how you use our platform.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-900 mb-2">Session Cookies</h4>
                        <p className="text-sm text-blue-800">
                          Temporary cookies that are deleted when you close your browser. Essential for 
                          maintaining your session and security.
                        </p>
                      </div>
                      
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-green-900 mb-2">Persistent Cookies</h4>
                        <p className="text-sm text-green-800">
                          Remain on your device for a set period or until manually deleted. Used to 
                          remember your preferences across visits.
                        </p>
                      </div>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <Info className="h-5 w-5 text-amber-600 mr-3 mt-0.5" />
                        <div className="text-amber-800">
                          <h4 className="font-semibold mb-1">Your Control</h4>
                          <p className="text-sm">
                            You have full control over cookies and can manage your preferences at any time 
                            through your browser settings or our cookie preferences panel.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Cookie Categories */}
              <section id="cookie-categories" className="mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Cookie Categories</h2>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {cookieTypes.map((type, index) => (
                      <div key={index} className={`border-2 rounded-lg p-6 ${
                        type.color === 'green' ? 'border-green-200 bg-green-50' :
                        type.color === 'blue' ? 'border-blue-200 bg-blue-50' :
                        type.color === 'purple' ? 'border-orange-200 bg-orange-50' :
                        'border-pink-200 bg-pink-50'
                      }`}>
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                              type.color === 'green' ? 'bg-green-100' :
                              type.color === 'blue' ? 'bg-blue-100' :
                              type.color === 'purple' ? 'bg-orange-100' :
                              'bg-pink-100'
                            }`}>
                              <type.icon className={`h-6 w-6 ${
                                type.color === 'green' ? 'text-green-600' :
                                type.color === 'blue' ? 'text-blue-600' :
                                type.color === 'purple' ? 'text-orange-600' :
                                'text-pink-600'
                              }`} />
                            </div>
                            <div className="ml-4">
                              <h3 className={`text-lg font-semibold ${
                                type.color === 'green' ? 'text-green-900' :
                                type.color === 'blue' ? 'text-blue-900' :
                                type.color === 'purple' ? 'text-orange-900' :
                                'text-pink-900'
                              }`}>
                                {type.title}
                              </h3>
                              <p className={`text-sm ${
                                type.color === 'green' ? 'text-green-700' :
                                type.color === 'blue' ? 'text-blue-700' :
                                type.color === 'purple' ? 'text-orange-700' :
                                'text-pink-700'
                              }`}>
                                {type.description}
                              </p>
                            </div>
                          </div>
                          
                          {type.required ? (
                            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                              Required
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                              Optional
                            </span>
                          )}
                        </div>
                        
                        <div>
                          <h4 className={`font-medium mb-2 ${
                            type.color === 'green' ? 'text-green-900' :
                            type.color === 'blue' ? 'text-blue-900' :
                            type.color === 'purple' ? 'text-orange-900' :
                            'text-pink-900'
                          }`}>
                            Examples:
                          </h4>
                          <ul className={`text-sm space-y-1 ${
                            type.color === 'green' ? 'text-green-800' :
                            type.color === 'blue' ? 'text-blue-800' :
                            type.color === 'purple' ? 'text-orange-800' :
                            'text-pink-800'
                          }`}>
                            {type.examples.map((example, idx) => (
                              <li key={idx} className="flex items-center">
                                <div className={`w-1 h-1 rounded-full mr-3 ${
                                  type.color === 'green' ? 'bg-green-600' :
                                  type.color === 'blue' ? 'bg-blue-600' :
                                  type.color === 'purple' ? 'bg-orange-600' :
                                  'bg-red-600'
                                }`}></div>
                                {example}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Third-Party Cookies */}
              <section id="third-party-cookies" className="mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Globe className="h-6 w-6 text-orange-500 mr-3" />
                    Third-Party Services
                  </h2>
                  
                  <p className="text-gray-700 mb-6">
                    We use trusted third-party services to enhance our platform. These services may set 
                    their own cookies when you interact with their content or features.
                  </p>

                  <div className="space-y-4">
                    {thirdPartyServices.map((service, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-semibold text-gray-900">{service.name}</h4>
                            <p className="text-sm text-gray-600">{service.purpose}</p>
                          </div>
                          <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                            service.type === 'Necessary' ? 'bg-red-100 text-red-700' :
                            service.type === 'Analytics' ? 'bg-orange-100 text-orange-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {service.type}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="font-medium text-gray-700">Data Shared:</span>
                            <p className="text-gray-600">{service.dataShared}</p>
                          </div>
                          <div>
                            <span className="font-medium text-gray-700">Opt-out:</span>
                            <p className="text-gray-600">{service.optOut}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Managing Cookies */}
              <section id="managing-cookies" className="mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <Settings className="h-6 w-6 text-orange-500 mr-3" />
                    Managing Your Cookies
                  </h2>
                  
                  <div className="text-gray-700 space-y-6">
                    <p>
                      You have several options for managing cookies and controlling your privacy preferences:
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                        <div className="flex items-center mb-3">
                          <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                          <h4 className="font-semibold text-green-900">StrymHub Cookie Controls</h4>
                        </div>
                        <ul className="text-sm text-green-800 space-y-1">
                          <li>• Use our cookie preference center</li>
                          <li>• Granular control over cookie categories</li>
                          <li>• Save preferences for future visits</li>
                          <li>• Easy opt-in/opt-out options</li>
                        </ul>
                      </div>

                      <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                        <div className="flex items-center mb-3">
                          <Settings className="h-6 w-6 text-blue-600 mr-3" />
                          <h4 className="font-semibold text-blue-900">Browser Settings</h4>
                        </div>
                        <ul className="text-sm text-blue-800 space-y-1">
                          <li>• Configure cookie settings in your browser</li>
                          <li>• Block third-party cookies</li>
                          <li>• Clear existing cookies</li>
                          <li>• Set cookie expiration preferences</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <Info className="h-5 w-5 text-amber-600 mr-3 mt-0.5" />
                        <div className="text-amber-800">
                          <h4 className="font-semibold mb-1">Important Note</h4>
                          <p className="text-sm">
                            Blocking certain cookies may affect your ability to use some features of our platform. 
                            Necessary cookies cannot be disabled as they are essential for basic functionality.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Browser Controls */}
              <section id="browser-controls" className="mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Browser-Specific Controls</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      { name: 'Chrome', instructions: 'Settings → Privacy and security → Cookies and other site data' },
                      { name: 'Firefox', instructions: 'Settings → Privacy & Security → Cookies and Site Data' },
                      { name: 'Safari', instructions: 'Preferences → Privacy → Manage Website Data' },
                      { name: 'Edge', instructions: 'Settings → Site permissions → Cookies and site data' },
                      { name: 'Opera', instructions: 'Settings → Advanced → Privacy & Security → Site Settings' },
                      { name: 'Internet Explorer', instructions: 'Tools → Internet Options → Privacy tab' }
                    ].map((browser, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                        <h4 className="font-semibold text-gray-900 mb-2">{browser.name}</h4>
                        <p className="text-sm text-gray-600">{browser.instructions}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Contact Information */}
              <section id="contact" className="mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions About Cookies?</h2>
                  <div className="text-gray-700 space-y-4">
                    <p>
                      If you have any questions about our use of cookies or this Cookie Policy, please contact us:
                    </p>
                    
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <Cookie className="h-5 w-5 text-orange-500 mr-3" />
                          <span><strong>Privacy Team:</strong> privacy@strymhub.com</span>
                        </div>
                        <div className="flex items-center">
                          <Settings className="h-5 w-5 text-orange-500 mr-3" />
                          <span><strong>Technical Support:</strong> info.strymhub@gmail.com</span>
                        </div>
                        <div className="flex items-center">
                          <Globe className="h-5 w-5 text-orange-500 mr-3" />
                          <span><strong>Location:</strong> London, United Kingdom</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Last Updated */}
              <div className="bg-orange-50 p-6 rounded-lg text-center">
                <p className="text-orange-700 font-medium">
                  This Cookie Policy was last updated on December 29, 2025
                </p>
                <p className="text-orange-600 text-sm mt-2">
                  Version: 1.0.0 | We will notify you of any significant changes to our cookie practices
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default CookiePolicyPage;